import { CHARACTERS, type CharacterId } from './characters';
import type { StatMod } from './types';

export type SkillBranch = 'offense' | 'survival' | 'signature';
export type SkillFlag = 'aegis_dash' | 'berserk' | 'overload' | 'deadeye' | 'phantom';

export interface SkillNodeDef {
  id: string;
  branch: SkillBranch;
  pos: { col: number; row: number };
  name: string;
  desc: string;
  maxRanks: number;
  cost: number;
  mods: StatMod[];
  flag?: SkillFlag;
  requires?: { all?: string[]; pointsInBranch?: number };
}

interface Seed {
  slug: string;
  name: string;
  desc: string;
  mods?: StatMod[];
  maxRanks?: number;
  cost?: number;
  points?: number;
  flag?: SkillFlag;
}

interface TreeSpec {
  id: CharacterId;
  offense: { root: string; capstone: string; rootMod: StatMod; precision: StatMod[]; force: StatMod[]; mastery: StatMod[]; capMods: StatMod[] };
  survival: { root: string; capstone: string; rootMod: StatMod; guard: StatMod[]; sustain: StatMod[]; mobility: StatMod[]; mastery: StatMod[]; capMods: StatMod[] };
  signature: Seed[];
}

const COL: Record<SkillBranch, number> = { offense: 0, survival: 1, signature: 2 };

function branch(character: CharacterId, kind: SkillBranch, seeds: Seed[]): SkillNodeDef[] {
  const root = `${character}_${kind}`;
  return seeds.map((seed, row) => ({
    id: row === 0 ? root : `${character}_${seed.slug}`,
    branch: kind,
    pos: { col: COL[kind], row },
    name: seed.name,
    desc: seed.desc,
    maxRanks: seed.maxRanks ?? (row === 0 ? 5 : row === seeds.length - 1 ? 1 : 2),
    cost: seed.cost ?? (row === seeds.length - 1 ? 3 : row === seeds.length - 2 ? 2 : 1),
    mods: seed.mods ?? [],
    flag: seed.flag,
    requires: row === 0 ? undefined : { all: [root], pointsInBranch: seed.points ?? (row < 3 ? 3 : row < 5 ? 6 : row === seeds.length - 1 ? 11 : 9) },
  }));
}

function tree(spec: TreeSpec): SkillNodeDef[] {
  const { id, offense: o, survival: s } = spec;
  return [
    ...branch(id, 'offense', [
      { slug: 'offense', name: o.root, desc: 'Increase core damage by 4% per rank.', mods: [o.rootMod] },
      { slug: 'offense_tempo', name: 'Combat Tempo', desc: '+4% fire rate per rank.', mods: [{ stat: 'fireRate', mul: 1.04 }] },
      { slug: 'offense_precision', name: 'Precision', desc: 'Improve this Timewalker’s preferred finishing stats.', mods: o.precision },
      { slug: 'offense_reach', name: 'Extended Reach', desc: '+7% range and projectile speed per rank.', mods: [{ stat: 'range', mul: 1.07 }, { stat: 'projectileSpeed', mul: 1.07 }] },
      { slug: 'offense_force', name: 'Focused Force', desc: 'Deepen the branch’s primary attack style.', mods: o.force },
      { slug: 'offense_mastery', name: 'Offense Mastery', desc: 'A major offensive specialization.', mods: o.mastery },
      { slug: 'offense_capstone', name: o.capstone, desc: 'Complete the offense branch.', mods: o.capMods },
    ]),
    ...branch(id, 'survival', [
      { slug: 'survival', name: s.root, desc: 'Increase maximum HP by 8 per rank.', mods: [s.rootMod] },
      { slug: 'survival_guard', name: 'Timeline Guard', desc: 'Reduce incoming pressure.', mods: s.guard },
      { slug: 'survival_sustain', name: 'Recovery Loop', desc: 'Improve sustained recovery.', mods: s.sustain },
      { slug: 'survival_mobility', name: 'Escape Vector', desc: 'Improve mobility and ability cycling.', mods: s.mobility },
      { slug: 'survival_radius', name: 'Wide Awareness', desc: '+12% pickup radius per rank.', mods: [{ stat: 'pickupRadius', mul: 1.12 }] },
      { slug: 'survival_mastery', name: 'Survival Mastery', desc: 'A major defensive specialization.', mods: s.mastery },
      { slug: 'survival_capstone', name: s.capstone, desc: 'Complete the survival branch.', mods: s.capMods },
    ]),
    ...branch(id, 'signature', spec.signature),
  ];
}

const SPECS: Record<CharacterId, TreeSpec> = {
  knight: {
    id: 'knight',
    offense: { root: 'Tempered Edge', capstone: 'Relentless Advance', rootMod: { stat: 'damageMult', mul: 1.04 }, precision: [{ stat: 'pierce', add: 1 }], force: [{ stat: 'critChance', add: 0.02 }], mastery: [{ stat: 'projectiles', add: 1 }], capMods: [{ stat: 'damageMult', mul: 1.15 }, { stat: 'fireRate', mul: 1.08 }] },
    survival: { root: 'Timeline Ward', capstone: 'Last Bastion', rootMod: { stat: 'maxHp', add: 8 }, guard: [{ stat: 'armor', add: 1 }], sustain: [{ stat: 'regen', add: 0.35 }], mobility: [{ stat: 'dashCooldown', mul: 0.94 }], mastery: [{ stat: 'maxHp', add: 20 }, { stat: 'armor', add: 1 }], capMods: [{ stat: 'maxHp', mul: 1.25 }, { stat: 'dashCharges', add: 1 }] },
    signature: [
      { slug: 'signature', name: 'Unbroken Guard', desc: '+0.04 seconds dash invulnerability per rank.', mods: [{ stat: 'dashIframes', add: 0.04 }] },
      { slug: 'long_stride', name: 'Long Stride', desc: '+8% dash distance per rank.', mods: [{ stat: 'dashDistance', mul: 1.08 }] },
      { slug: 'battle_rhythm', name: 'Battle Rhythm', desc: 'Fire at +12% rate while dashing per rank.', mods: [{ stat: 'dashFireMult', add: 0.12 }] },
      { slug: 'quick_reset', name: 'Quick Reset', desc: 'Dash recharges 7% faster per rank.', mods: [{ stat: 'dashCooldown', mul: 0.93 }] },
      { slug: 'guardians_reach', name: 'Guardian’s Reach', desc: '+15% movement speed.', mods: [{ stat: 'moveSpeed', mul: 1.15 }] },
      { slug: 'keystone', name: 'Aegis Dash', desc: 'Dashing grants a one-hit shield; 4 second cooldown.', cost: 3, flag: 'aegis_dash' },
    ],
  },
  barbarian: {
    id: 'barbarian',
    offense: { root: 'Savage Force', capstone: 'World Breaker', rootMod: { stat: 'damageMult', mul: 1.05 }, precision: [{ stat: 'critChance', add: 0.02 }], force: [{ stat: 'projectileSize', mul: 1.1 }, { stat: 'damageMult', mul: 1.03 }], mastery: [{ stat: 'pierce', add: 2 }], capMods: [{ stat: 'damageMult', mul: 1.2 }, { stat: 'fireRate', mul: 0.95 }] },
    survival: { root: 'Iron Blood', capstone: 'Too Angry to Fall', rootMod: { stat: 'maxHp', add: 10 }, guard: [{ stat: 'armor', add: 0.75 }], sustain: [{ stat: 'lifesteal', add: 0.0075 }], mobility: [{ stat: 'moveSpeed', mul: 1.05 }], mastery: [{ stat: 'maxHp', add: 25 }], capMods: [{ stat: 'maxHp', mul: 1.3 }, { stat: 'armor', add: 2 }] },
    signature: [
      { slug: 'signature', name: 'Crimson Momentum', desc: '+0.75% lifesteal per rank.', mods: [{ stat: 'lifesteal', add: 0.0075 }] },
      { slug: 'roaring_power', name: 'Roaring Power', desc: '+6% damage per rank.', mods: [{ stat: 'damageMult', mul: 1.06 }] },
      { slug: 'roaring_recovery', name: 'Roaring Recovery', desc: 'Ability recharges 5% faster per rank.', mods: [{ stat: 'dashCooldown', mul: 0.95 }] },
      { slug: 'predators_stride', name: 'Predator’s Stride', desc: '+6% movement speed per rank.', mods: [{ stat: 'moveSpeed', mul: 1.06 }] },
      { slug: 'bloodstorm', name: 'Bloodstorm', desc: '+12% fire rate.', mods: [{ stat: 'fireRate', mul: 1.12 }] },
      { slug: 'keystone', name: 'Berserk', desc: 'Below 40% HP, gain +30% damage and +20% fire rate.', cost: 3, flag: 'berserk' },
    ],
  },
  mage: {
    id: 'mage',
    offense: { root: 'Arcane Pressure', capstone: 'Grand Synthesis', rootMod: { stat: 'damageMult', mul: 1.04 }, precision: [{ stat: 'lightningChance', add: 0.025 }], force: [{ stat: 'fireChance', add: 0.025 }, { stat: 'frostChance', add: 0.025 }], mastery: [{ stat: 'lightningChains', add: 1 }], capMods: [{ stat: 'damageMult', mul: 1.15 }, { stat: 'fireChance', mul: 1.15 }, { stat: 'frostChance', mul: 1.15 }, { stat: 'lightningChance', mul: 1.15 }, { stat: 'temporalChance', mul: 1.15 }] },
    survival: { root: 'Runic Shell', capstone: 'Impossible Geometry', rootMod: { stat: 'maxHp', add: 7 }, guard: [{ stat: 'temporalChance', add: 0.025 }], sustain: [{ stat: 'regen', add: 0.3 }], mobility: [{ stat: 'dashCooldown', mul: 0.94 }], mastery: [{ stat: 'temporalRewind', add: 4 }], capMods: [{ stat: 'maxHp', mul: 1.25 }, { stat: 'moveSpeed', mul: 1.15 }] },
    signature: [
      { slug: 'signature', name: 'Elemental Focus', desc: '+2% elemental proc chances per rank.', mods: [{ stat: 'fireChance', add: 0.02 }, { stat: 'frostChance', add: 0.02 }, { stat: 'lightningChance', add: 0.02 }, { stat: 'temporalChance', add: 0.02 }] },
      { slug: 'nova_velocity', name: 'Nova Velocity', desc: '+8% projectile speed per rank.', mods: [{ stat: 'projectileSpeed', mul: 1.08 }] },
      { slug: 'nova_mass', name: 'Nova Mass', desc: '+8% projectile size per rank.', mods: [{ stat: 'projectileSize', mul: 1.08 }] },
      { slug: 'nova_cycle', name: 'Nova Cycle', desc: 'Ability recharges 7% faster per rank.', mods: [{ stat: 'dashCooldown', mul: 0.93 }] },
      { slug: 'perfect_attunement', name: 'Perfect Attunement', desc: '+15% range and +1 pierce.', mods: [{ stat: 'range', mul: 1.15 }, { stat: 'pierce', add: 1 }] },
      { slug: 'keystone', name: 'Overload', desc: 'Every fourth Arcane Nova fires a second rotated nova.', cost: 3, flag: 'overload' },
    ],
  },
  ranger: {
    id: 'ranger',
    offense: { root: 'True Shot', capstone: 'One Perfect Shot', rootMod: { stat: 'damageMult', mul: 1.04 }, precision: [{ stat: 'critChance', add: 0.02 }], force: [{ stat: 'pierce', add: 1 }], mastery: [{ stat: 'critMult', add: 0.25 }], capMods: [{ stat: 'damageMult', mul: 1.15 }, { stat: 'critChance', add: 0.05 }] },
    survival: { root: 'Trailcraft', capstone: 'Never Cornered', rootMod: { stat: 'maxHp', add: 6 }, guard: [{ stat: 'armor', add: 0.75 }], sustain: [{ stat: 'regen', add: 0.35 }], mobility: [{ stat: 'moveSpeed', mul: 1.06 }, { stat: 'dashCooldown', mul: 0.97 }], mastery: [{ stat: 'pickupRadius', mul: 1.25 }], capMods: [{ stat: 'maxHp', mul: 1.2 }, { stat: 'dashCooldown', mul: 0.8 }] },
    signature: [
      { slug: 'signature', name: 'Far Sight', desc: '+2% crit chance per rank.', mods: [{ stat: 'critChance', add: 0.02 }] },
      { slug: 'volley_speed', name: 'Volley Speed', desc: '+10% projectile speed per rank.', mods: [{ stat: 'projectileSpeed', mul: 1.1 }] },
      { slug: 'volley_reach', name: 'Volley Reach', desc: '+10% range per rank.', mods: [{ stat: 'range', mul: 1.1 }] },
      { slug: 'volley_cycle', name: 'Volley Cycle', desc: 'Ability recharges 7% faster per rank.', mods: [{ stat: 'dashCooldown', mul: 0.93 }] },
      { slug: 'hunters_focus', name: 'Hunter’s Focus', desc: '+1 pierce and +10% critical damage.', mods: [{ stat: 'pierce', add: 1 }, { stat: 'critMult', add: 0.1 }] },
      { slug: 'keystone', name: 'Deadeye', desc: 'Standing still for 1 second arms a guaranteed critical shot.', cost: 3, flag: 'deadeye' },
    ],
  },
  rogue: {
    id: 'rogue',
    offense: { root: 'Opportunist', capstone: 'No Witnesses', rootMod: { stat: 'damageMult', mul: 1.04 }, precision: [{ stat: 'critChance', add: 0.02 }], force: [{ stat: 'movingFireMult', add: 0.05 }], mastery: [{ stat: 'bounce', add: 2 }], capMods: [{ stat: 'damageMult', mul: 1.15 }, { stat: 'fireRate', mul: 1.12 }] },
    survival: { root: 'Fleetfoot', capstone: 'Untouchable', rootMod: { stat: 'maxHp', add: 6 }, guard: [{ stat: 'dashIframes', add: 0.04 }], sustain: [{ stat: 'regen', add: 0.25 }], mobility: [{ stat: 'moveSpeed', mul: 1.07 }, { stat: 'dashCooldown', mul: 0.97 }], mastery: [{ stat: 'armor', add: 2 }], capMods: [{ stat: 'maxHp', mul: 1.2 }, { stat: 'dashCooldown', mul: 0.8 }] },
    signature: [
      { slug: 'signature', name: 'Between Seconds', desc: '+4% moving fire rate per rank.', mods: [{ stat: 'movingFireMult', add: 0.04 }] },
      { slug: 'long_step', name: 'Long Step', desc: '+7% movement speed per rank.', mods: [{ stat: 'moveSpeed', mul: 1.07 }] },
      { slug: 'phase_arsenal', name: 'Phase Arsenal', desc: '+8% damage per rank.', mods: [{ stat: 'damageMult', mul: 1.08 }] },
      { slug: 'phase_cycle', name: 'Phase Cycle', desc: 'Ability recharges 7% faster per rank.', mods: [{ stat: 'dashCooldown', mul: 0.93 }] },
      { slug: 'ghost_fire', name: 'Ghost Fire', desc: 'Fire at +20% rate while dashing.', mods: [{ stat: 'dashFireMult', add: 0.2 }] },
      { slug: 'keystone', name: 'Phantom', desc: 'Kills within 0.6 seconds of Phase Step reset its cooldown.', cost: 3, flag: 'phantom' },
    ],
  },
};

export const SKILL_TREES = Object.fromEntries(CHARACTERS.map((character) => [character.id, tree(SPECS[character.id])])) as Record<CharacterId, SkillNodeDef[]>;
export const SKILL_NODE_BY_ID = new Map(Object.values(SKILL_TREES).flat().map((node) => [node.id, node]));

export interface ResolvedSkillBuild { allocated: Record<string, number>; spent: number; mods: StatMod[]; flags: SkillFlag[] }

function branchPoints(treeNodes: SkillNodeDef[], allocated: Record<string, number>, kind: SkillBranch): number {
  return treeNodes.filter((node) => node.branch === kind).reduce((sum, node) => sum + (allocated[node.id] ?? 0) * node.cost, 0);
}

function requirementsMet(treeNodes: SkillNodeDef[], node: SkillNodeDef, allocated: Record<string, number>): boolean {
  if (node.requires?.all?.some((id) => (allocated[id] ?? 0) <= 0)) return false;
  return branchPoints(treeNodes, allocated, node.branch) >= (node.requires?.pointsInBranch ?? 0);
}

/** Resolve untrusted allocations in topological order, enforcing costs, gates and the level budget. */
export function resolveSkillBuild(character: CharacterId, raw: Record<string, number> | undefined, pointBudget = Number.POSITIVE_INFINITY): ResolvedSkillBuild {
  const treeNodes = SKILL_TREES[character];
  const allocated: Record<string, number> = {};
  const mods: StatMod[] = [];
  const flags = new Set<SkillFlag>();
  let spent = 0;
  for (const node of treeNodes) {
    const wanted = Math.max(0, Math.min(node.maxRanks, Math.floor(Number(raw?.[node.id]) || 0)));
    let rank = 0;
    while (rank < wanted && spent + node.cost <= pointBudget && requirementsMet(treeNodes, node, allocated)) {
      allocated[node.id] = ++rank;
      spent += node.cost;
      mods.push(...node.mods);
    }
    if (rank > 0 && node.flag) flags.add(node.flag);
  }
  return { allocated, spent, mods, flags: [...flags] };
}

export function validAllocated(character: CharacterId, raw: Record<string, number>, pointBudget = Number.POSITIVE_INFINITY): Record<string, number> {
  return resolveSkillBuild(character, raw, pointBudget).allocated;
}

export function canAllocateSkill(character: CharacterId, allocated: Record<string, number>, nodeId: string, pointBudget: number): boolean {
  const node = SKILL_NODE_BY_ID.get(nodeId);
  if (!node || !node.id.startsWith(`${character}_`) || (allocated[nodeId] ?? 0) >= node.maxRanks) return false;
  const before = resolveSkillBuild(character, allocated, pointBudget);
  const attempted = { ...before.allocated, [nodeId]: (before.allocated[nodeId] ?? 0) + 1 };
  return (resolveSkillBuild(character, attempted, pointBudget).allocated[nodeId] ?? 0) > (before.allocated[nodeId] ?? 0);
}

export function emptyCharacterProgress(): Record<CharacterId, { xp: number; allocated: Record<string, number> }> {
  return Object.fromEntries(CHARACTERS.map((character) => [character.id, { xp: 0, allocated: {} }])) as Record<CharacterId, { xp: number; allocated: Record<string, number> }>;
}
